import { ABILITIES } from "./abilities";
import { CLASSES } from "./classes";
import { STATUSES } from "./statuses";

export const combat = {
  critMult: 2,
  fleetMoveMult: 1.4,
  frenzyCdMult: 0.65,
  crippleMoveMult: 0.5,
  maimOutMult: 0.7,
  woundHealMult: 1,
  corpseHp: 3,
  baselineHp: 10,
  autoEnabled: 1,
  autoRequiresStill: 0,
  frontDot: 0,
};

export const binds = {
  skill: "shift",
  ult: " ",
  board: "tab",
  tuner: "`",
};

export const COMBAT_FIELDS: { key: keyof typeof combat; label: string; step: number; hint: string }[] = [
  { key: "baselineHp", label: "Hero baseline HP", step: 1, hint: "Human default. Class max HP can still override for PvE." },
  { key: "critMult", label: "Crit multiplier", step: 0.1, hint: "Damage multiplier on crit." },
  { key: "fleetMoveMult", label: "Fleet move mult", step: 0.05, hint: "1.4 = +40% speed." },
  { key: "frenzyCdMult", label: "Frenzy auto CD mult", step: 0.05, hint: "0.65 = 35% faster autos." },
  { key: "crippleMoveMult", label: "Cripple move mult", step: 0.05, hint: "0.5 = half speed." },
  { key: "maimOutMult", label: "Maim outgoing mult", step: 0.05, hint: "Damage dealt while Maimed." },
  { key: "woundHealMult", label: "Wound incoming heal", step: 0.05, hint: "1 = no heal penalty. Wound also applies Bleeding." },
  { key: "corpseHp", label: "Corpse HP", step: 1, hint: "Harvestable leftover Health." },
  { key: "autoEnabled", label: "Auto-attacks on", step: 1, hint: "1 on, 0 off. Fires at lock or nearest." },
  { key: "autoRequiresStill", label: "Auto only if still", step: 1, hint: "1 = Archero-style, no auto while moving." },
  { key: "frontDot", label: "Front/facing cosine", step: 0.05, hint: "0 = 180° frontal. Higher is a narrower cone." },
];

export const ABILITY_NUM_KEYS = [
  "cd",
  "range",
  "windup",
  "windupMove",
  "radius",
  "arc",
  "damage",
  "splash",
  "heal",
  "projectileSpeed",
  "projectileRadius",
  "projectileCount",
  "projectileSpread",
  "dashDistance",
  "chargeSpeed",
  "delay",
  "zoneDuration",
] as const;

export const CLASS_NUM_KEYS = ["maxHp", "speed", "basicCd"] as const;

export const STATUS_NUM_KEYS = ["tickEvery", "tickDamage", "tickHeal"] as const;

type Snapshot = {
  combat: typeof combat;
  binds: typeof binds;
  classes: Record<string, Record<string, number>>;
  abilities: Record<string, Record<string, number>>;
  statuses: Record<string, Record<string, number>>;
};

function pickNums(obj: object, keys: readonly string[]): Record<string, number> {
  const rec = obj as Record<string, unknown>;
  const out: Record<string, number> = {};
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "number") out[k] = v;
  }
  return out;
}

function snapshot(): Snapshot {
  const classes: Snapshot["classes"] = {};
  for (const [id, def] of Object.entries(CLASSES)) classes[id] = pickNums(def, CLASS_NUM_KEYS);
  const abilities: Snapshot["abilities"] = {};
  for (const [id, def] of Object.entries(ABILITIES)) abilities[id] = pickNums(def, ABILITY_NUM_KEYS);
  const statuses: Snapshot["statuses"] = {};
  for (const [id, def] of Object.entries(STATUSES)) statuses[id] = pickNums(def, STATUS_NUM_KEYS);
  return {
    combat: { ...combat },
    binds: { ...binds },
    classes,
    abilities,
    statuses,
  };
}

const DEFAULTS = snapshot();
const STORAGE_KEY = "court-tuning-v1";

function applyNums(target: Record<string, unknown>, nums: Record<string, number> | undefined): void {
  if (!nums) return;
  for (const [k, v] of Object.entries(nums)) {
    if (typeof v === "number" && typeof target[k] === "number") target[k] = v;
  }
}

export function applySnapshot(data: Partial<Snapshot>): void {
  if (data.combat) Object.assign(combat, data.combat);
  if (data.binds) Object.assign(binds, data.binds);
  if (data.classes) {
    for (const [id, nums] of Object.entries(data.classes)) {
      if (CLASSES[id as keyof typeof CLASSES]) applyNums(CLASSES[id as keyof typeof CLASSES] as unknown as Record<string, unknown>, nums);
    }
  }
  if (data.abilities) {
    for (const [id, nums] of Object.entries(data.abilities)) {
      if (ABILITIES[id]) applyNums(ABILITIES[id] as unknown as Record<string, unknown>, nums);
    }
  }
  if (data.statuses) {
    for (const [id, nums] of Object.entries(data.statuses)) {
      if (STATUSES[id as keyof typeof STATUSES]) applyNums(STATUSES[id as keyof typeof STATUSES] as unknown as Record<string, unknown>, nums);
    }
  }
}

export function resetTuning(): void {
  applySnapshot(JSON.parse(JSON.stringify(DEFAULTS)) as Snapshot);
  persistTuning();
}

export function persistTuning(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot()));
  } catch {
    /* ignore quota */
  }
}

export function loadTuning(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    applySnapshot(JSON.parse(raw) as Partial<Snapshot>);
  } catch {
    /* ignore bad payload */
  }
}

export function applyBaselineHpToHeroes(): void {
  for (const def of Object.values(CLASSES)) {
    if (def.playable) def.maxHp = combat.baselineHp;
  }
  persistTuning();
}

export function bindLabel(key: string): string {
  if (key === " ") return "SPACE";
  if (key === "`") return "`";
  return key.toUpperCase();
}
